import { useState } from 'react';
import { ChevronRightIcon, XIcon } from 'lucide-react';
import type { ActionDefinition } from '@mockd/shared/types/rule';
import { HttpRequestConfig } from '../actions/configs/HttpRequestConfig';
import { SetVariableConfig } from '../actions/configs/SetVariableConfig';
import { StoreVariableConfig } from '../actions/configs/StoreVariableConfig';

interface ActionDefinitionEditorProps {
  action: ActionDefinition;
  index: number;
  onChange: (action: ActionDefinition) => void;
  onRemove: () => void;
}

const ACTION_TYPES = [
  { value: 'http_request', label: 'HTTP Request' },
  { value: 'set_variable', label: 'Set Variable' },
  { value: 'store_variable', label: 'Store Variable' },
] as const;

const ACTION_TYPE_BADGES: Record<string, { label: string; className: string }> = {
  http_request: { label: 'HTTP', className: 'badge-info' },
  set_variable: { label: 'Set Var', className: 'badge-warning' },
  store_variable: { label: 'Store Var', className: 'badge-warning' },
};

function ActionTypeConfigDispatcher({
  type,
  config,
  onChange,
}: {
  type: ActionDefinition['type'];
  config: Record<string, unknown>;
  onChange: (config: Record<string, unknown>) => void;
}) {
  switch (type) {
    case 'http_request':
      return <HttpRequestConfig config={config} onChange={onChange} />;
    case 'set_variable':
      return <SetVariableConfig config={config} onChange={onChange} />;
    case 'store_variable':
      return <StoreVariableConfig config={config} onChange={onChange} />;
    default:
      return null;
  }
}

export function ActionDefinitionEditor({
  action,
  index,
  onChange,
  onRemove,
}: ActionDefinitionEditorProps) {
  const [collapsed, setCollapsed] = useState(false);

  const badge = ACTION_TYPE_BADGES[action.type] || { label: action.type, className: 'badge-ghost' };

  return (
    <div className="border border-base-300 rounded-box">
      <div
        className="flex items-center justify-between px-3 py-2 cursor-pointer hover:bg-base-200/50"
        onClick={() => setCollapsed(!collapsed)}
      >
        <div className="flex items-center gap-2">
          <ChevronRightIcon className={`size-4 transition-transform ${collapsed ? '' : 'rotate-90'}`} />
          <span className="text-sm font-medium">Action {index + 1}</span>
          <span className={`badge badge-sm ${badge.className}`}>{badge.label}</span>
        </div>

        <div onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            onClick={onRemove}
            className="btn btn-ghost btn-xs btn-square text-error"
            title="Remove action"
            aria-label="Remove action"
          >
            <XIcon className="size-4" />
          </button>
        </div>
      </div>

      {!collapsed && (
        <div className="px-3 pb-3 space-y-3 border-t border-base-200 mt-0">
          <div className="grid grid-cols-3 gap-3 mt-3">
            <div className="fieldset min-w-0">
              <label className="fieldset-legend">Type</label>
              <select
                value={action.type}
                onChange={(e) => onChange({
                  ...action,
                  type: e.target.value as ActionDefinition['type'],
                  config: {},
                })}
                className="select select-sm w-full"
              >
                {ACTION_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>

            <div className="fieldset min-w-0">
              <label className="fieldset-legend">On Error</label>
              <select
                value={action.onError}
                onChange={(e) => onChange({ ...action, onError: e.target.value as 'continue' | 'stop' })}
                className="select select-sm w-full"
              >
                <option value="continue">Continue</option>
                <option value="stop">Stop</option>
              </select>
            </div>

            <div className="fieldset min-w-0">
              <label className="fieldset-legend">Timeout (ms)</label>
              <input
                type="number"
                value={action.timeoutMs || ''}
                onChange={(e) => onChange({
                  ...action,
                  timeoutMs: e.target.value ? parseInt(e.target.value, 10) : undefined,
                })}
                placeholder="5000"
                min="100"
                className="input input-sm w-full"
              />
            </div>
          </div>

          <ActionTypeConfigDispatcher
            type={action.type}
            config={action.config}
            onChange={(config) => onChange({ ...action, config })}
          />
        </div>
      )}
    </div>
  );
}
